import type { FlagReason, PaymentStatus } from './order-shared.model';

export type { FlagReason, PaymentStatus };

export type PurchaseOrderStatus =
  | 'Draft'
  | 'Open'
  | 'Partially Received'
  | 'Received'
  | 'Completed'
  | 'Cancelled';

export type PurchaseOrderType = 'Delivery by vendor' | 'Pickup at vendor';

export interface PurchaseOrderItem {
  id: string;
  productId: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  isInventoryItem: boolean;
  receivedQuantity: number;
  pallets: number;
  unitsPerPallet: number;
  weightLb: number;
  /** Extra $ added on top of break-even cost when suggesting a sale price for this lot. */
  markupAmount: number;
  /** Excludes the item from being sold/assigned until released. */
  onHold: boolean;
  /** When set, this item's suggested sale price ignores the break-even calculation. */
  skipBreakEvenPriceSuggestion: boolean;
  /** Descriptive tags (e.g. "GAP Certified", "Local") searched/added per line. */
  labels?: string[];
  /** Country of origin, e.g. "MX". */
  country?: string;
  isOrganic?: boolean;
  /** Manually chosen lot number; used instead of an auto-generated one when the order is received. */
  plannedLotNumber?: string;
  /** Warehouse Management: spot the lot is put in when the line is received. */
  storageSpotId?: string;
  packDate?: string;
  useByDate?: string;
  /** Non-inventory items only: expense category and GL cost account, matching how Expenses are classified. */
  category?: string;
  costAccountNumber?: string;
  /** Set once the item has been received and a lot has been generated for it. */
  generatedLot?: {
    id: string;
    number: string;
    /** Warehouse Management: spot the lot is stored in. */
    storageSpotId?: string;
    /** The lot is on hold: it can't be assigned to sales orders until released. */
    onHold?: boolean;
    /** The lot was closed: its remaining units were wasted and it can't be sold. */
    isClosed?: boolean;
  };
}

/** Item of a return to shipper (quantities and credits are positive; shown as negatives). */
export interface PurchaseOrderReturnItem {
  purchaseOrderItemId: string;
  /** Lot the returned inventory left (inventory items only). */
  lotId?: string;
  quantity: number;
  creditPerUnit: number;
  total: number;
}

/**
 * Return to shipper: items sent back to the vendor (or credited) and the credit received.
 * Returned inventory leaves its lot; an applied credit lowers the order's payable balance.
 */
export interface PurchaseOrderReturn {
  id: string;
  returnDate: string;
  vendorInvoiceNumber?: string;
  bolNumber?: string;
  shippingReferenceNumber?: string;
  /** Credit on the whole order, not tied to an item. */
  orderCredit: number;
  /** Item credits plus the order credit. */
  total: number;
  noteToVendor?: string;
  /** Whether the credit is applied to the purchase order's balance. */
  isApplied: boolean;
  createdByName?: string;
  createdAt?: string;
  items: PurchaseOrderReturnItem[];
}

export interface PurchaseOrder {
  id: string;
  number: string;
  vendorId: string;
  warehouseId: string;
  orderType: PurchaseOrderType;
  orderDate: string;
  expectedDate?: string;
  receivedDate?: string;
  status: PurchaseOrderStatus;
  /** A zero-value PO represents a PAS/consignment transaction (spec section 14). */
  isConsignment: boolean;
  total: number;
  /** Total ancillary expenses tied to this PO (freight, inspection, etc.), allocated
   *  per inventory unit to derive each item's break-even cost. */
  expensesTotal: number;
  vendorInvoiceNumber?: string;
  bolNumber?: string;
  shippingReferenceNumber?: string;
  signedOff: boolean;
  paymentStatus: PaymentStatus;
  /** True when payments have been applied to the order (editing then needs "edit after payments"). */
  hasPayments?: boolean;
  buyerName: string;
  receivedByName?: string;
  flagged: boolean;
  flagReason?: FlagReason;
  flagNote?: string;
  flaggedBy?: string;
  flaggedAt?: string;
  notes?: string;
  /** Note printed on the purchase order for the vendor. */
  noteToVendor?: string;
  /** Returns to shipper (credits received from the vendor). */
  returns?: PurchaseOrderReturn[];
  items: PurchaseOrderItem[];
}

/** Quantity of a line returned to the vendor / credited. */
export function returnedQuantityOf(order: PurchaseOrder, itemId: string): number {
  return (order.returns ?? []).reduce(
    (sum, r) => sum + r.items.filter((i) => i.purchaseOrderItemId === itemId).reduce((s, i) => s + i.quantity, 0),
    0,
  );
}

/** Credits of returns to shipper applied to the order's balance. */
export function appliedReturnCredits(order: PurchaseOrder): number {
  return (order.returns ?? []).filter((r) => r.isApplied).reduce((sum, r) => sum + (r.total ?? 0), 0);
}
